import { useEffect, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { CheckCircle, Error } from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import {
  useValidateEmailVerificationTokenQuery,
  useConfirmEmailVerificationMutation,
} from "features/auth/auth";
import CustomButton from "./common/CustomButton";

export default function VerifyEmail() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [verificationStatus, setVerificationStatus] = useState<"loading" | "success" | "error">("loading");
  const [errorMessage, setErrorMessage] = useState("");

  const { data: tokenData, isLoading: isValidating, error: tokenError } =
    useValidateEmailVerificationTokenQuery(token || "", { skip: !token });

  const [confirmVerification] = useConfirmEmailVerificationMutation();

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token || isValidating) return;

      if (tokenError || !tokenData?.valid) {
        setVerificationStatus("error");
        setErrorMessage("This verification link is invalid or has expired.");
        return;
      }

      try {
        await confirmVerification({ token }).unwrap();
        setVerificationStatus("success");
      } catch (error: any) {
        setVerificationStatus("error");
        setErrorMessage(error?.data?.message || "Failed to verify email. Please try again.");
      }
    };

    if (!isValidating && tokenData) {
      verifyEmail();
    } else if (!isValidating && tokenError) {
      setVerificationStatus("error");
      setErrorMessage("This verification link is invalid or has expired.");
    }
  }, [token, tokenData, tokenError, isValidating, confirmVerification]);

  if (verificationStatus === "loading") {
    return (
      <Box
        marginTop={12}
        display="flex"
        flexDirection="column"
        alignItems="center"
        sx={{
          width: "100%",
          maxWidth: 400,
          boxSizing: "border-box",
          px: { xs: 2, sm: 3 },
          bgcolor: "#fff",
          p: { xs: 2, sm: 4 },
          borderRadius: 1.5,
          border: "1px solid #e0e0e0",
          mx: "auto",
        }}
      >
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="h6" sx={{ color: "rgb(31, 93, 107)" }}>
          Verifying your email...
        </Typography>
      </Box>
    );
  }

  if (verificationStatus === "error") {
    return (
      <Box
        marginTop={12}
        display="flex"
        flexDirection="column"
        alignItems="center"
        sx={{
          width: "100%",
          maxWidth: 400,
          boxSizing: "border-box",
          px: { xs: 2, sm: 3 },
          bgcolor: "#fff",
          p: { xs: 2, sm: 4 },
          borderRadius: 1.5,
          border: "1px solid #e0e0e0",
          mx: "auto",
        }}
      >
        <Error sx={{ fontSize: 64, color: "#f44336", mb: 2 }} />
        <Typography variant="h5" color="error" gutterBottom textAlign="center">
          Verification Failed
        </Typography>
        <Typography color="text.secondary" textAlign="center" mb={3}>
          {errorMessage}
        </Typography>
        <CustomButton onClick={() => navigate("/login")}>
          Go to Login
        </CustomButton>
      </Box>
    );
  }

  return (
    <Box
      marginTop={12}
      display="flex"
      flexDirection="column"
      alignItems="center"
      sx={{
        width: "100%",
        maxWidth: 400,
        boxSizing: "border-box",
        px: { xs: 2, sm: 3 },
        bgcolor: "#fff",
        p: { xs: 2, sm: 4 },
        borderRadius: 1.5,
        border: "1px solid #e0e0e0",
        mx: "auto",
      }}
    >
      <CheckCircle sx={{ fontSize: 64, color: "#4caf50", mb: 2 }} />
      <Typography
        variant="h5"
        sx={{ color: "rgb(31, 93, 107)", mb: 2, textAlign: "center" }}
      >
        Email Verified
      </Typography>
      <Typography color="text.secondary" textAlign="center" mb={3}>
        Your email has been successfully verified. You can now use it for password recovery.
      </Typography>
      <CustomButton onClick={() => navigate("/")}>
        Continue
      </CustomButton>
    </Box>
  );
}
